import { useState } from 'react';
import { CheckCircleOutlined, MobileOutlined, PlusOutlined, StopOutlined } from '@ant-design/icons';
import { Alert, App, Button, Drawer, Form, Input, Modal, Space, Tag } from 'antd';
import { api, showMobile } from '../../api';
import { ActiveTag, DataTable, ErrorNotice, PageHeader, Panel, Person, RowActions } from '../../components';
import { formatDate, MOBILE_RULES, timeAgo, useLoad } from '../../lib';
import { BackToSettings } from './BackToSettings';
export function AdminUsersSettings() {
    const { message } = App.useApp();
    const [adding, setAdding] = useState(false);
    const [saving, setSaving] = useState(false);
    const [mobileFor, setMobileFor] = useState(null);
    const [addForm] = Form.useForm();
    const [mobileForm] = Form.useForm();
    const { data, error, loading, reload } = useLoad(() => api('/v1/admin/admins'));
    const admins = data?.admins ?? [];
    const me = data?.me;
    async function add(values) {
        setSaving(true);
        try {
            await api('/v1/admin/admins', { method: 'POST', body: JSON.stringify(values) });
            message.success(`${values.name} can now sign in to the admin console`);
            setAdding(false);
            addForm.resetFields();
            await reload();
        }
        catch (err) {
            message.error(err instanceof Error ? err.message : 'Could not add the admin');
        }
        finally {
            setSaving(false);
        }
    }
    async function toggle(row) {
        try {
            await api(`/v1/admin/admins/${row.id}/${row.disabledAt ? 'enable' : 'disable'}`, { method: 'POST' });
            message.success(row.disabledAt ? `${row.name} can sign in again` : `${row.name} is disabled and signed out`);
            await reload();
        }
        catch (err) {
            message.error(err instanceof Error ? err.message : 'Could not change the admin');
        }
    }
    async function changeMobile(values) {
        if (!mobileFor)
            return;
        setSaving(true);
        try {
            const { admin } = await api(`/v1/admin/admins/${mobileFor.id}/mobile`, { method: 'POST', body: JSON.stringify({ mobile: values.mobile }) });
            message.success(mobileFor.id === me ? `You now sign in with ${showMobile(admin.mobile)}` : `${mobileFor.name} now signs in with ${showMobile(admin.mobile)}`);
            setMobileFor(null);
            mobileForm.resetFields();
            await reload();
        }
        catch (err) {
            message.error(err instanceof Error ? err.message : 'Could not change the mobile number');
        }
        finally {
            setSaving(false);
        }
    }
    return (<>
      <PageHeader title="Admin users" description="People who can sign in to this admin console. Every change they make is in the audit log." actions={<>
            <BackToSettings />
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setAdding(true)}>
              Add admin
            </Button>
          </>}/>
      <ErrorNotice error={error}/>

      <Panel flush>
        <DataTable rowKey="id" loading={loading} skeletonRows={2} dataSource={admins} pagination={false} scroll={{ x: 820 }} columns={[
            {
                title: 'Admin',
                skeleton: 'person',
                render: (_value, row) => (<Person name={row.name} detail={[showMobile(row.mobile), row.email].filter(Boolean).join(' · ')} badge={row.id === me && <Tag color="purple" variant="filled" style={{ marginInlineStart: 8, fontWeight: 500 }}>You</Tag>}/>)
            },
            { title: 'Added', dataIndex: 'createdAt', width: 140, render: (value) => formatDate(value) },
            { title: 'Last sign-in', dataIndex: 'lastSignInAt', width: 150, render: (value) => (value ? timeAgo(value) : 'Never') },
            { title: 'Status', dataIndex: 'disabledAt', width: 120, skeleton: 'tag', render: (value) => <ActiveTag active={!value}/> },
            {
                title: '',
                width: 56,
                align: 'right',
                skeleton: 'actions',
                render: (_value, row) => (<RowActions label={`Actions for ${row.name}`} actions={[
                        { key: 'mobile', icon: <MobileOutlined />, label: row.id === me ? 'Change my mobile' : 'Change mobile', hint: 'Sign-in codes go to the new number', onClick: () => setMobileFor(row) },
                        row.id !== me &&
                            (row.disabledAt
                                ? { key: 'enable', icon: <CheckCircleOutlined />, label: 'Enable admin', hint: 'They can sign in again', onClick: () => toggle(row) }
                                : {
                                    key: 'disable',
                                    icon: <StopOutlined />,
                                    label: 'Disable admin',
                                    danger: true,
                                    hint: 'Signs them out at once',
                                    confirm: {
                                        title: 'Disable this admin?',
                                        description: `${row.name} is signed out at once and cannot sign in until enabled again.`,
                                        okText: 'Disable'
                                    },
                                    onClick: () => toggle(row)
                                })
                    ]}/>)
            }
        ]}/>
      </Panel>

      <Panel title="Locked out?" description="If no admin can sign in.">
        <p style={{ margin: 0, color: 'var(--body)', lineHeight: 1.6 }}>
          On the API server, run <span className="mono">npm run admin -- --mobile 98XXXXXXXX --name "Your Name"</span> in the BACKEND folder. It creates the
          admin with that number (or enables it again if it exists). Sign-in codes appear in the API server's log until an SMS service is connected.
        </p>
      </Panel>

      <Drawer title="Add admin" open={adding} onClose={() => setAdding(false)} size={440} destroyOnHidden footer={<Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => setAdding(false)}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={() => addForm.submit()}>
              Add admin
            </Button>
          </Space>}>
        <Alert type="info" showIcon title="Admins can see and change everything in this console, including keys under Settings." style={{ marginBottom: 20 }}/>
        <Form form={addForm} layout="vertical" requiredMark={false} onFinish={add}>
          <Form.Item label="Full name" name="name" rules={[{ required: true, message: 'Enter the name.' }]}>
            <Input placeholder="Asha Mehta" autoFocus/>
          </Form.Item>
          <Form.Item label="Mobile number" name="mobile" rules={MOBILE_RULES} extra="They sign in with this number and a one-time code sent to it.">
            <Input prefix={<MobileOutlined style={{ color: 'var(--muted)' }}/>} placeholder="98765 43210" inputMode="tel" autoComplete="off" maxLength={16}/>
          </Form.Item>
          <Form.Item label="Email" name="email" rules={[{ type: 'email', message: 'Enter a valid email.' }]} extra="Optional, for your records.">
            <Input placeholder="name@company.com" autoComplete="off"/>
          </Form.Item>
        </Form>
      </Drawer>

      <Modal title={mobileFor?.id === me ? 'Change my mobile number' : `Change the mobile number of ${mobileFor?.name ?? ''}`} open={Boolean(mobileFor)} onCancel={() => setMobileFor(null)} onOk={() => mobileForm.submit()} okText="Save number" confirmLoading={saving} destroyOnHidden>
        <p style={{ color: 'var(--muted)', marginTop: 0 }}>
          Sign-in codes go to the new number from now on{mobileFor ? `; ${showMobile(mobileFor.mobile)} no longer signs in` : ''}.
        </p>
        <Form form={mobileForm} layout="vertical" requiredMark={false} onFinish={changeMobile}>
          <Form.Item label="New mobile number" name="mobile" rules={MOBILE_RULES}>
            <Input prefix={<MobileOutlined style={{ color: 'var(--muted)' }}/>} placeholder="98765 43210" inputMode="tel" autoFocus autoComplete="off" maxLength={16}/>
          </Form.Item>
        </Form>
      </Modal>
    </>);
}
